//
//  activity_tracking.js
//  Cell-Ed
//
//  Copyright © 2020 Cell-Ed. All rights reserved.
//

import ActivityTrackingManagerIOS from "./activity_tracking_ios";
import ActivityTrackingManagerAndroid from "./activity_tracking_android";
import { capIsAndroid } from "../capacitor_compat";

/**
 * Picks the pipeline by what the native app supports, not by platform.
 *
 * Current Android and iOS builds expose one ActivityTracking API (configureNetworking,
 * startPassiveCollection, stopPassiveCollection, getHealthAuthorization), which
 * activity_tracking_android.js drives. iOS 3.8.x predates it -- it needs the separate
 * PassiveNetworking plugin -- and runs on Capacitor 2, which publishes no plugin headers. Learners who
 * have not updated still run it, so it keeps its own pipeline.
 */
function nativeManagerClass() {
	const headers = window.Capacitor && window.Capacitor.PluginHeaders;
	if (Array.isArray(headers)) {
		const plugin = headers.find((header) => header.name === 'ActivityTracking');
		const sharesApi = Boolean(plugin && (plugin.methods || []).some((method) => method.name === 'configureNetworking'));
		return sharesApi ? ActivityTrackingManagerAndroid : ActivityTrackingManagerIOS;
	}
	return capIsAndroid() ? ActivityTrackingManagerAndroid : ActivityTrackingManagerIOS;
}
/**
 * ActivityTrackingManager is a ReactJS-client library
 * to interface with the Capacitor native plug-ins responsible
 * for activity tracking.
 */
export default class ActivityTrackingManager {
	constructor() {
		const Manager = nativeManagerClass();
		this.instance = new Manager();
	}

	/**
	 * Configuring iOS for Activity Tracking requires defining
	 * network endpoints for background URL sessions
	 * @param {String} learnerId
	 * @return {Boolean} success/failure
	 */
	async configure(learnerId) {
		return await this.instance.configure(learnerId);
	}

	/**
	 * Start tracking activity in the background.
	 * Calls configure() for session before starting.
	 * Must call getPermissions before calling start.
	 * @param {String} learnerId
	 * @return {Boolean} success/failure
	 */
	async start(learnerId) {
		return await this.instance.start(learnerId);
	}

	/**
	 * STOP tracking activity in the background.
	 * @return {Boolean} success/failure
	 */
	async stop(userId) {
		return await this.instance.stop(userId);
	}

	/**
	 * Request native permissions to HealthKit (iOS) activity data.
	 * Permissions must be obtained before starting data collection.
	 * @return {Boolean} true if permissions were granted.
	 */
	async getPermissions(userId) {
		return await this.instance.getPermissions(userId);
	}
}